import { AuthServiceV4 } from '@oneaudi/audi-auth-service';
import { TFootnote } from '@oneaudi/confi-shared-components';
import { I18NServiceV1 } from '@oneaudi/i18n-service';
import { NumberFormatterServiceV1 } from '@oneaudi/number-formatter-service/dist/v1';
import {
	CarPrice,
	ConfiguredCarCarCode,
	ConfiguredCarCatalogModel,
	ConfiguredCarFeature,
	ConfiguredCarPrices,
	FinancingModelParameters,
	Maybe,
	MonthlyRate,
	Scalars,
	TechnicalDataConsumptionAndEmissionSummary,
} from '@oneaudi/onegraph-client';
import { NotificationProps } from '@oneaudi/unified-web-components';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import { AemMarketConfig } from '~/app/market-config';
import { ConfiguredCarFinancing, TypedPrice } from '~/graphql/__generated__/graphql';
import { fetchTranslations, Translations } from '../../i18n/i18n';

export interface Keys {
	applyButtonLabel: string;
	priceIncludingVat: string;
	includingVat: string;
	pricePerMonth: string;
	priceDetails: string;
	privateLeasing: string;
	totalPricePrefix?: string;
}

export type AudiCode = {
	data: ConfiguredCarCarCode;
	loading: boolean;
};

export type State = {
	vehicleData?: VehicleData;
	modelName?: string;
	selectedEngine?: ConfiguredCarCatalogModel | null;
	featureEquipment?: FeatureEquipment[];
	featureGroupEquipment?: FeatureGroupEquipment[];
	additionalOptions?: ConfiguredCarFeature[];
	prices?: ConfiguredCarPrices;
	priceDetails?: PriceDetails;
	pdfUrl?: string;
	audiCode?: AudiCode;
	loadingStatus: LoadingStatus;
	translations: Translations;
	content: AemMarketConfig;
	isStage: boolean;
	numberFormatterService: NumberFormatterServiceV1;
	isLoggedIn: boolean;
	notificationData?: NotificationProps;
	financingData?: FinancingData;
};

export interface PriceDetailsConfigurations {
	optionsPrice: {
		formattedValue: Maybe<string> | undefined;
	};
	modelPrice: {
		formattedValue: Maybe<string> | undefined;
	};
	taxTotal: {
		formattedValue: Maybe<string> | undefined;
	};
}

export interface VehicleData {
	monthlyRate: Maybe<MonthlyRate> | undefined;
	total: Maybe<CarPrice> | undefined;
	vehicleName: string;
	modelName: string;
	productName: string;
	engineFootnote?: TFootnote;
	priceDetailsConfigurations: PriceDetailsConfigurations;
	priceParts: Array<TypedPrice>;
	calculationDisclaimer: Maybe<string> | undefined;
	pdfUrl: string;
	audiCode: string;
	modelIdentifier?: FinancingModelParameters | undefined;
	priceDate: {
		text: Maybe<string> | undefined;
	};
}

export interface FinancingData {
	financing: Partial<ConfiguredCarFinancing>;
	loading: boolean;
}

export interface RenderedCarImage {
	profileId: string;

	url: string;

	fallbackUrlUsed: boolean;

	mimeType: string;

	renderServiceId: string;

	width: Scalars['Int'];

	altTextMetaKeys: string[];

	deviceType: RenderedImagesDeviceType;
}

export interface FeatureEquipment {
	key: string;
	selectedFeature: ConfiguredCarFeature;
}

export interface FeatureGroupEquipment {
	key: string;
	selectedGroup: ConfiguredCarFeature[] | undefined;
}

export interface PriceDetails {
	total: Maybe<CarPrice> | undefined;
	monthlyRate: Maybe<MonthlyRate> | undefined;
	vehicleName: string;
	modelName: string;
	consumptionAndEmission?: Maybe<TechnicalDataConsumptionAndEmissionSummary> | undefined;
	priceDetailsConfigurations: PriceDetailsConfigurations;
	priceDate: {
		text: Maybe<string> | undefined;
	};
}

enum RenderedImagesDeviceType {
	'MOBILE',
}

export enum LoadingStatus {
	INITIAL,
	UPDATE,
	LOADED,
}

export type AsyncStateHolder = (() => Promise<State>) | State;

export async function createInitialState(
	localeService: GfaLocaleServiceV1,
	i18nService: I18NServiceV1,
	numberFormatterService: NumberFormatterServiceV1,
	content: AemMarketConfig,
	isStage: boolean,
	authService: AuthServiceV4,
): Promise<State> {
	const [translations, isLoggedIn] = await Promise.all([
		fetchTranslations(localeService, i18nService),
		authService.isAuthenticated(),
	]);
	return {
		translations,
		vehicleData: undefined,
		loadingStatus: LoadingStatus.INITIAL,
		numberFormatterService,
		content,
		isStage,
		isLoggedIn,
	};
}

export type Action =
	| {
			type: 'UPDATE_LOADING_STATUS';
			payload: LoadingStatus;
	  }
	| {
			type: 'UPDATE_VEHICLE_DATA';
			payload: VehicleData;
	  }
	| {
			type: 'SET_CONTENT';
			payload: AemMarketConfig;
	  }
	| {
			type: 'SET_AUDICODE';
			payload: AudiCode;
	  }
	| {
			type: 'UPDATE_SELECTED_EQUIPMENT';
			payload: {
				modelName: string;
				selectedEngine: ConfiguredCarCatalogModel | undefined | null;
				featureEquipment: FeatureEquipment[];
				featureGroupEquipment: FeatureGroupEquipment[];
				additionalOptions: ConfiguredCarFeature[] | undefined;
				prices: ConfiguredCarPrices;
				priceDetails: PriceDetails;
				pdfUrl: string;
				audiCode: string;
			};
	  }
	| {
			type: 'UPDATE_ACCESS_TOKEN';
			payload: string;
	  }
	| {
			type: 'UPDATE_LOGIN_STATUS';
			payload: boolean;
	  }
	| {
			type: 'SET_NOTIFICATION_DATA';
			payload: NotificationProps | undefined;
	  }
	| {
			type: 'SET_FINANCING_DATA';
			payload: FinancingData | undefined;
	  };

export default function reducer(state: State, action: Action): State {
	switch (action.type) {
		case 'UPDATE_LOADING_STATUS':
			return {
				...state,
				loadingStatus: action.payload,
			};
		case 'UPDATE_VEHICLE_DATA':
			return {
				...state,
				vehicleData: action.payload,
			};
		case 'SET_CONTENT':
			return {
				...state,
				content: action.payload,
			};
		case 'SET_AUDICODE':
			return {
				...state,
				audiCode: action.payload,
			};
		case 'UPDATE_SELECTED_EQUIPMENT':
			return {
				...state,
				modelName: action.payload.modelName,
				selectedEngine: action.payload.selectedEngine,
				featureEquipment: action.payload.featureEquipment,
				featureGroupEquipment: action.payload.featureGroupEquipment,
				additionalOptions: action.payload.additionalOptions,
				prices: action.payload.prices,
				priceDetails: action.payload.priceDetails,
				pdfUrl: action.payload.pdfUrl,
				// audiCode: action.payload.audiCode,
			};
		case 'UPDATE_LOGIN_STATUS':
			return {
				...state,
				isLoggedIn: action.payload,
			};
		case 'SET_NOTIFICATION_DATA':
			return {
				...state,
				notificationData: action.payload,
			};
		case 'SET_FINANCING_DATA':
			return {
				...state,
				financingData: action.payload,
			};
		default:
			// eslint-disable-next-line no-console
			console.warn('Unknown action: ', action);
			return state;
	}
}
